import type { ParsedUrlQuery } from 'node:querystring'
import { P, match } from 'ts-pattern'
import { z } from 'zod'

import type { PageType } from '@/cms/pages'
import type { WebUrl } from '@/cms/webUrl'
import { DOMAIN, WEB_BUYING_FLOW_DOMAIN, isDev } from '@/config/env'
import { PERMANENT_SEARCH_PARAMS } from '@/constants/url'
import { reportWarning } from '@/services/errorReporting'

const LOCALE_PATTERN = /^\/[a-z]{2}-[a-z]{2}\//

export const getPageName = (pagePath: string): string => {
  // remove the locale from the path
  // in:  /versicherungen/dental
  // out: versicherungen/dental
  const splittedPath = pagePath.split('/')
  return splittedPath.slice(1).join('/')
}

export const getQueryParams = (requestURL: string): Record<string, string> => {
  const queryString = requestURL.split('?')[1]
  const searchParams = new URLSearchParams(queryString)
  return Object.fromEntries(searchParams)
}

export const getBuyingFlowUrl = ({
  flowName,
  campaign,
  coupon,
  extraParams,
  locale,
  useLandingPageHost = false,
}: {
  flowName: string
  locale: string
  campaign?: string
  coupon?: string
  extraParams?: URLSearchParams
  useLandingPageHost?: boolean
}): string => {
  const funnelHost = `https://${WEB_BUYING_FLOW_DOMAIN}/${locale}/flows/${flowName}`
  const landingPageHost = `http${isDev() ? '' : 's'}://${DOMAIN}/${locale}/flows/${flowName}`

  const hostDomain = useLandingPageHost ? landingPageHost : funnelHost

  const url = new URL(hostDomain)

  if (campaign) {
    url.searchParams.append('campaign', campaign)
  }
  if (coupon) {
    url.searchParams.append('coupon', coupon)
  }

  if (extraParams) {
    extraParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.append(key, value)
      }
    })
  }

  return url.href
}

export const removeImpermanentSearchParams = (
  searchParams: ParsedUrlQuery | URLSearchParams
): URLSearchParams => {
  const filtered = new URLSearchParams(
    // This typecast is valid since that this actually what query is
    searchParams as Record<string, string>
  )
  PERMANENT_SEARCH_PARAMS.forEach((param) => {
    if (filtered.has(param)) {
      filtered.delete(param)
    }
  })
  return filtered
}

export const openInNewTab = (webUrl: WebUrl) =>
  webUrl && '_type' in webUrl[0] && ['pdf', 'externalWebsite'].includes(webUrl?.[0]?._type)

export const noFollow = (webUrl: WebUrl) =>
  webUrl &&
  '_type' in webUrl[0] &&
  (webUrl?.[0]?._type === 'buyingFlow' || webUrl?.[0]?._type === 'pdf')

export const getProductButtonTarget = (pageType?: PageType, locale?: string) => {
  if (!pageType || !locale) {
    return
  }

  return match(pageType)
    .with(
      {
        _type: 'website.pageType.productPage',
        abstractProduct: {
          webBuyingFlow: P.string,
        },
      },
      (narrowedPageType) => {
        const { webBuyingFlow } = narrowedPageType.abstractProduct
        if (webBuyingFlow) {
          return getBuyingFlowUrl({
            flowName: webBuyingFlow,
            locale,
            useLandingPageHost: true,
          })
        } else if (!webBuyingFlow) {
          reportWarning(
            `Header: webBuyingFlow slug not set for abstractProduct ${narrowedPageType.abstractProduct.abstractProductKey}`
          )
          return
        }
        throw new Error('Assertion: Empty abstract product information for pageType')
      }
    )
    .with(
      {
        _type: 'website.pageType.bundleProductPage',
        bundleProductPage: {
          bundleName: P.string,
        },
      },
      (narrowedPageType) => {
        const { bundleName, flowName } = narrowedPageType
        if (bundleName) {
          return getBuyingFlowUrl({
            flowName: flowName,
            locale,
            useLandingPageHost: true,
          })
        }
        throw new Error('Assertion: Empty abstract product information for pageType')
      }
    )
    .with(
      {
        _type: 'website.pageType.mgaLightProductPage',
        mgaLightProduct: {
          webBuyingFlow: P.string.or(P.nullish),
          appointmentLink: P.string.or(P.nullish),
        },
      },
      (narrowedPageType) => {
        const { webBuyingFlow, appointmentLink } = narrowedPageType.mgaLightProduct
        if (webBuyingFlow) {
          return getBuyingFlowUrl({
            flowName: webBuyingFlow,
            locale,
            useLandingPageHost: true,
          })
        }

        if (appointmentLink) {
          return appointmentLink
        }

        throw new Error(
          `Assertion: Neither "webBuyingFlow", nor "appointmentLink" properties are defined for the product "${narrowedPageType.mgaLightProduct.mgaLightProductKey}"`
        )
      }
    )
    .otherwise(() => undefined)
}

export const getPageUrl = (path: string, locale?: string) => {
  const url = new URL(`/${locale}/${path}`, `https://${DOMAIN}`)

  return url.toString()
}

export const getPathWithParams = (
  path: string,
  { params, anchor = '' }: { params?: string; anchor?: string } = {}
) => {
  if (!params) {
    return `${path}${anchor}`
  }
  return `${path}?${params}${anchor}`
}

const isWebUrl = (url: string) => {
  return z.string().url().safeParse(url).success
}

export const getAppendedFullUrl = (webUrl: string, whitelistedParams: string) => {
  const url = new URL(webUrl)
  const params = new URLSearchParams(whitelistedParams)
  if (isWebUrl(webUrl)) {
    for (const param of params.entries()) {
      if (!url.searchParams.has(param[0])) {
        url.searchParams.append(param[0], param[1])
      }
    }
  }
  return url.toString()
}

/**
 * Normalizes a path for analytics tracking by:
 * - Stripping query strings and hash fragments
 * - Ensuring locale prefix is present
 * - Extracting UTM parameters for separate tracking
 *
 * @param path - The path to normalize (can include query string)
 * @param locale - The locale for the path (e.g., 'de-de', 'en-de')
 * @returns Object with normalized path and extracted UTM parameters
 */
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export const normalizePathForTracking = (
  path: string,
  locale?: string
): {
  normalizedPath: string
  utmParams: Record<string, string>
  search: string
} => {
  const url = new URL(path, `https://${DOMAIN}`)

  let normalizedPath = url.pathname

  const hasLocalePrefix = LOCALE_PATTERN.test(normalizedPath)

  if (!hasLocalePrefix && locale) {
    normalizedPath = `/${locale}${normalizedPath}`
  }

  const utmParams: Record<string, string> = {}
  UTM_KEYS.forEach((key) => {
    const value = url.searchParams.get(key)
    if (value) {
      utmParams[key] = value
    }
  })

  return {
    normalizedPath,
    utmParams,
    search: url.search.slice(1),
  }
}

export const getFlowNameFromPath = (path: string): string | undefined => {
  const [, flowName] = /\/flows\/([^#/?]+)/.exec(path) ?? []
  return flowName
}
